/**
 * Donor Accounts (the sidebar's Users & Donors group).
 *
 * The sister of Users & Access, for the other door. That page administers people who
 * arrive through Cloudflare Zero Trust and may be given any role; this one administers
 * people who arrive at the donor hostname with a password, and can only ever be one
 * thing — the view-only account the archive already has.
 *
 * The distinction worth keeping in mind while reading this file is that there is no
 * role control on it, and that is not an omission. A donor's permissions are not
 * stored anywhere, so there is no field to expose here: they are assembled from the
 * viewer role at each request and then narrowed (see donorPrincipal in
 * src/worker/donorAuth.js). This page can create an account, switch it off, and send
 * its owner a link. It cannot make one into an editor, because nothing can.
 *
 * The other thing this page does that its sister does not is hand over a one-time
 * link. Email may not be configured, or may fail, and neither must mean "nobody can be
 * given access today" — so every action that mints a link shows it, ready to copy.
 */
import { useState } from 'react'
import { PageHeader } from '../App'
import { api, useApi } from '../lib/api'
import {
  Card, Field, Badge, Modal, Switch, Spinner, Notice, QueryState,
  ConfirmModal, SearchInput, useToast, fmtDateTime, fmtInt,
} from '../components/ui'
import { useRelabel } from '../lib/terms'

/**
 * The four states an account can be in, in the order they matter. Switched off beats
 * everything — a disabled account that is also locked is disabled, and saying "locked"
 * would send an administrator to the wrong button.
 */
function state(row) {
  if (!row.is_active) return { label: 'Disabled', tone: 'danger', hint: 'Cannot sign in. Their sessions were ended.' }
  if (row.is_locked) return { label: 'Locked', tone: 'warn', hint: 'Too many failed sign-in attempts. Clears itself, or unlock it here.' }
  if (!row.has_password) return { label: 'Invited', tone: undefined, hint: 'Sent an invitation, not yet accepted. The account authorizes nobody until they choose a password.' }
  return { label: 'Active', tone: 'ok', hint: 'Signed up and able to sign in.' }
}

export default function SettingsDonors({ user }) {
  const t = useRelabel()
  const query = useApi('donors', 'donors', { enabled: user.caps.manageUsers })
  const toast = useToast()
  const [addOpen, setAddOpen] = useState(false)
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  /* The other way to add one: pick a donor the giving register already knows. Its own
     dialogue rather than a mode of the one above, because it asks a different question —
     "which of these people" instead of "what is their address". */
  const [pickOpen, setPickOpen] = useState(false)
  const [pickQ, setPickQ] = useState('')
  const [picked, setPicked] = useState(null)
  const [syncOpen, setSyncOpen] = useState(false)
  /* Fetched only while the picker is open: it is several hundred donor addresses, and
     there is no reason for a page view that never opens the dialogue to carry them. */
  const candidates = useApi(
    ['donor-candidates', pickQ],
    `donors/candidates${pickQ ? `?q=${encodeURIComponent(pickQ)}` : ''}`,
    { enabled: user.caps.manageUsers && pickOpen, keepPreviousData: true }
  )
  const [busy, setBusy] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(null)
  const [editing, setEditing] = useState(null) // { row, name }
  // The one-time link from whichever action last minted one, held so it can be copied.
  const [link, setLink] = useState(null)

  if (!user.caps.manageUsers) {
    return (
      <PageHeader title="Donor Accounts">
        <Notice tone="warn" icon="lock" title="Not available to your role">
          Only owners can manage donor accounts. You are signed in as{' '}
          <strong>{user.roleLabel}</strong>.
        </Notice>
      </PageHeader>
    )
  }

  /** Every action that mints a link ends here: report the send, then show the link. */
  const handleLink = (res, { email: to, kind }) => {
    setLink({ url: res.link, email: to, kind, emailed: res.emailed, error: res.mailError })
    if (res.emailed) toast.ok(`Sent ${to} a link to ${kind === 'invite' ? 'set their password' : 'reset their password'}`)
    else toast.info(`Nothing was emailed — copy the link and send it to ${to} yourself`)
    query.refetch()
  }

  const add = async () => {
    setBusy(true)
    try {
      const res = await api('donors', {
        method: 'POST',
        body: { email, display_name: name },
      })
      setAddOpen(false)
      setEmail('')
      setName('')
      handleLink(res, { email, kind: 'invite' })
    } catch (err) {
      toast.error(err)
    } finally {
      setBusy(false)
    }
  }

  /**
   * Create an account for a donor the register already knows.
   *
   * Only the register's id goes up: the address and the name are read from the register
   * on the Worker's side, which is the point of picking from a list rather than typing.
   * The units they have given to become the units they follow, and the count comes back
   * so the toast can say how many.
   */
  const addFromRegister = async () => {
    if (!picked) return
    setBusy(true)
    try {
      const res = await api('donors', { method: 'POST', body: { registry_id: picked.id } })
      setPickOpen(false)
      setPicked(null)
      setPickQ('')
      handleLink(res, { email: picked.email, kind: 'invite' })
      if (res.followed) {
        toast.ok(
          `Following ${fmtInt(res.followed)} ${t(res.followed === 1 ? 'partner unit' : 'partner units')} they have given to`
        )
      }
    } catch (err) {
      toast.error(err)
    } finally {
      setBusy(false)
    }
  }

  /**
   * Bring one account's favourites up to date with the register.
   *
   * Adds only — see the route. The toast says both halves, because "3 added" without
   * "2 of their own left alone" reads as though the other two were lost.
   */
  const syncOne = async (row) => {
    try {
      const res = await api(`donors/${row.id}/sync-favorites`, { method: 'POST' })
      if (res.added) {
        toast.ok(
          `${row.email} now follows ${fmtInt(res.added)} more ${t(
            res.added === 1 ? 'partner unit' : 'partner units'
          )}` + (res.kept ? `, and keeps ${fmtInt(res.kept)} of their own` : '')
        )
      } else {
        toast.info(`${row.email} already follows every ${t('partner unit')} the register has for them`)
      }
      query.refetch()
    } catch (err) {
      toast.error(err)
    }
  }

  /** The same across every account at once. */
  const syncAll = async () => {
    setBusy(true)
    try {
      const res = await api('donors/sync-favorites', { method: 'POST' })
      setSyncOpen(false)
      if (res.added) {
        toast.ok(
          `${fmtInt(res.added)} ${t(res.added === 1 ? 'partner unit' : 'partner units')} added across ` +
            `${fmtInt(res.accounts)} account${res.accounts === 1 ? '' : 's'}`
        )
      } else {
        toast.info('Every account already matches the register')
      }
      query.refetch()
    } catch (err) {
      toast.error(err)
    } finally {
      setBusy(false)
    }
  }

  const sendLink = async (row) => {
    // Invited accounts get the invitation again; accounts with a password get a reset.
    // Two letters that say different things, so the page picks rather than the server.
    const kind = row.has_password ? 'reset' : 'invite'
    try {
      handleLink(await api(`donors/${row.id}/${kind}`, { method: 'POST' }), { email: row.email, kind })
    } catch (err) {
      toast.error(err)
    }
  }

  const act = async (row, action, done) => {
    try {
      await api(`donors/${row.id}/${action}`, { method: 'POST' })
      toast.ok(done)
      query.refetch()
    } catch (err) {
      toast.error(err)
    }
  }

  const update = async (row, patch) => {
    try {
      await api(`donors/${row.id}`, { method: 'PUT', body: patch })
      query.refetch()
    } catch (err) {
      toast.error(err)
    }
  }

  const saveName = async () => {
    setBusy(true)
    try {
      await api(`donors/${editing.row.id}`, { method: 'PUT', body: { display_name: editing.name } })
      toast.ok('Name saved')
      setEditing(null)
      query.refetch()
    } catch (err) {
      toast.error(err)
    } finally {
      setBusy(false)
    }
  }

  const remove = async () => {
    setBusy(true)
    try {
      const res = await api(`donors/${confirmDelete.id}`, { method: 'DELETE' })
      // A donor holds no Zero Trust seat, so the first message is the usual one. The
      // second only appears for an address that had also come through the staff door
      // at some point and was still being billed for it.
      toast.ok(
        res?.seat?.released
          ? 'Donor account removed, and the Zero Trust seat this address was holding freed'
          : 'Donor account removed'
      )
      if (res?.seat?.error) toast.error(`A Zero Trust seat for that address could not be freed: ${res.seat.error}`)
      setConfirmDelete(null)
      query.refetch()
    } catch (err) {
      toast.error(err)
    } finally {
      setBusy(false)
    }
  }

  const copy = async (url) => {
    try {
      await navigator.clipboard.writeText(url)
      toast.ok('Link copied')
    } catch {
      // Clipboard access is refused in some browsers unless the page is focused, and
      // the link is on screen and selectable anyway.
      toast.info('Copy the link from the box above')
    }
  }

  return (
    <PageHeader
      title="Donor Accounts"
      sub="View-only accounts that sign in with a password, outside Cloudflare Zero Trust"
      actions={
        <>
          {/* Only when some account is actually behind the register — the count is on the
              button, so it is an offer to do a known amount of work rather than a verb. */}
          {!!query.data?.pending?.accounts && (
            <button className="btn btn-sm" onClick={() => setSyncOpen(true)}>
              Sync {query.data.pending.units} from the register
            </button>
          )}
          <button className="btn btn-sm" onClick={() => setPickOpen(true)}>
            + From the Giving Register
          </button>
          <button className="btn btn-sm btn-primary" onClick={() => setAddOpen(true)}>
            + Add Donor
          </button>
        </>
      }
    >
      <QueryState query={query}>
        {(d) => {
          const invited = d.rows.filter((r) => r.is_active && !r.has_password)
          const locked = d.rows.filter((r) => r.is_active && r.is_locked)
          return (
            <div className="col">
              <Notice icon="key" title="How a donor signs in">
                Donors do <strong>not</strong> use Cloudflare Zero Trust. They go to{' '}
                {d.config.donorUrl ? (
                  <a href={d.config.donorUrl} target="_blank" rel="noreferrer">
                    <code>{d.config.donorUrl.replace(/^https?:\/\//, '')}</code>
                  </a>
                ) : (
                  <em>the donor address</em>
                )}{' '}
                and sign in with their email address and a password this application holds — so they
                need no seat in Zero Trust and never appear on the Users &amp; Access page. Accounts
                cannot be created by the people who hold them: an account exists because somebody on
                this page made one.
              </Notice>

              {!d.config.donorUrl && (
                <Notice tone="danger" icon="warning" title="The donor address is not configured">
                  <code>DONOR_HOST</code> is unset in <code>wrangler.jsonc</code>, so the donor door
                  is closed and an invitation would have nowhere to point. Accounts cannot be created
                  until it is set and the Worker redeployed.
                </Notice>
              )}

              {!d.config.mailConfigured && (
                <Notice tone="warn" icon="mail" title="Email is not configured">
                  Invitations and password resets cannot be sent, so this page shows you the one-time
                  link to pass on yourself. A donor who forgets their password cannot recover it
                  without you until this is set — the <code>send_email</code> binding and{' '}
                  <code>MAIL_FROM</code> in <code>wrangler.jsonc</code> are what turn it on.
                </Notice>
              )}

              {invited.length ? (
                <Notice icon="pending" title={`${invited.length} invitation${invited.length > 1 ? 's' : ''} not yet accepted`}>
                  {invited.map((r) => r.email).join(', ')} — the account authorizes nobody until they
                  choose a password. Invitations expire after seven days; send another if one has.
                </Notice>
              ) : null}

              {locked.length ? (
                <Notice tone="warn" icon="lock" title={`${locked.length} locked out`}>
                  {locked.map((r) => r.email).join(', ')} — too many failed sign-in attempts. The
                  lockout clears itself after a few minutes, or you can clear it now.
                </Notice>
              ) : null}

              {link && (
                <Notice
                  tone={link.emailed ? undefined : 'warn'}
                  icon="link"
                  title={
                    link.emailed
                      ? `Link emailed to ${link.email}`
                      : `Send this link to ${link.email} yourself`
                  }
                >
                  {!link.emailed && link.error && (
                    <div className="small subtle" style={{ marginBottom: 8 }}>
                      Email was not sent: {link.error}
                    </div>
                  )}
                  <div className="row" style={{ gap: 8, alignItems: 'center' }}>
                    <input
                      className="input mono small"
                      readOnly
                      value={link.url}
                      onFocus={(e) => e.target.select()}
                      style={{ flex: 1, minWidth: 0 }}
                    />
                    <button className="btn btn-sm" onClick={() => copy(link.url)}>
                      Copy
                    </button>
                    <button className="btn btn-sm btn-ghost" onClick={() => setLink(null)}>
                      Done
                    </button>
                  </div>
                  <div className="small subtle" style={{ marginTop: 8 }}>
                    It works once and expires{' '}
                    {link.kind === 'invite' ? 'in seven days' : 'in one hour'}. Anyone holding it can
                    set the password on that account, so send it the way you would a password.
                  </div>
                </Notice>
              )}

              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Name</th>
                      <th>State</th>
                      <th>Signed in</th>
                      <th>Last sign-in</th>
                      <th>Added by</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {d.rows.length === 0 && (
                      <tr>
                        <td colSpan={7} className="subtle" style={{ padding: 18, textAlign: 'center' }}>
                          No donor accounts yet.
                        </td>
                      </tr>
                    )}
                    {d.rows.map((r) => {
                      const s = state(r)
                      return (
                        <tr key={r.id}>
                          <td>
                            <span className="mono small">{r.email}</span>
                          </td>
                          <td>{r.display_name ?? <span className="subtle">—</span>}</td>
                          <td>
                            <Badge tone={s.tone} title={s.hint}>
                              {s.label}
                            </Badge>
                          </td>
                          <td className="small">
                            {r.session_count ? (
                              <span title="Browsers currently holding a valid session">
                                {r.session_count}
                              </span>
                            ) : (
                              <span className="subtle">—</span>
                            )}
                          </td>
                          <td className="small nowrap">
                            {r.last_login_at ? (
                              fmtDateTime(r.last_login_at)
                            ) : (
                              <span className="subtle">never</span>
                            )}
                          </td>
                          <td className="small mono subtle">{r.created_by}</td>
                          {/* Wide enough that the row of controls stays on one line on
                              a desktop; it still wraps rather than overflowing when the
                              table is squeezed, and .table-wrap scrolls past that. */}
                          <td style={{ minWidth: 300 }}>
                            <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                              <button
                                className="btn btn-sm"
                                onClick={() => setEditing({ row: r, name: r.display_name ?? '' })}
                                title="Change the name this account is shown and greeted by"
                              >
                                Edit
                              </button>
                              <button
                                className="btn btn-sm"
                                onClick={() => sendLink(r)}
                                disabled={!r.is_active || !d.config.donorUrl}
                                title={
                                  r.has_password
                                    ? 'Email them a link to choose a new password'
                                    : 'Send the invitation again'
                                }
                              >
                                {r.has_password ? 'Reset' : 'Re-invite'}
                              </button>
                              {/* `!!` because SQLite hands these back as 0 and 1, and
                                  `0 && <button/>` is `0`, which React renders as a
                                  stray digit between the buttons rather than nothing. */}
                              {/* Only where the register has units this account is not
                                  following. An account already in step, or one the register
                                  has never heard of, gets no button rather than a button
                                  that does nothing. */}
                              {!!r.not_following && (
                                <button
                                  className="btn btn-sm"
                                  onClick={() => syncOne(r)}
                                  title={`Follow the ${r.not_following} ${t(
                                    r.not_following === 1 ? 'partner unit' : 'partner units'
                                  )} the register has for them and they are not following. Nothing is removed.`}
                                >
                                  Sync {r.not_following}
                                </button>
                              )}
                              {!!r.is_locked && (
                                <button
                                  className="btn btn-sm"
                                  onClick={() => act(r, 'unlock', `Lockout cleared for ${r.email}`)}
                                  title="Clear the failed sign-in lockout now"
                                >
                                  Unlock
                                </button>
                              )}
                              {!!r.session_count && (
                                <button
                                  className="btn btn-sm"
                                  onClick={() => act(r, 'signout', `${r.email} signed out everywhere`)}
                                  title="End every session without changing their password"
                                >
                                  Sign out
                                </button>
                              )}
                              <Switch
                                checked={!!r.is_active}
                                onChange={(on) => update(r, { is_active: on })}
                                title={
                                  r.is_active
                                    ? 'Switch this account off and end its sessions'
                                    : 'Let this account sign in again'
                                }
                              />
                              <button
                                className="btn btn-sm btn-danger"
                                onClick={() => setConfirmDelete(r)}
                              >
                                Remove
                              </button>
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              <Card title="What a Donor Account Can See">
                {/* A Card does not relabel its children, and this paragraph names the
                    records twice — so it says so itself. */}
                <p className="small">
                  {t(
                    <>
                      Exactly what the <strong>Viewer</strong> role sees, and it cannot be given
                      anything else: the dashboard, the updates, the prayer requests, the
                      highlighted needs, the partner units and their updates report, and
                      Appearance. Photographs reach them anonymized.
                    </>
                  )}
                </p>
                <p className="small subtle" style={{ marginTop: 10 }}>
                  There is no role to choose here because a donor has no stored permissions to
                  change. They are worked out from the Viewer role on every request and then
                  narrowed, so an edit to that role — or to any row in the database — cannot widen
                  what a donor sees by one page. The media library, the countries, the other
                  reports, the audit log, the settings and everything under AI answer them with a
                  refusal, whichever way they are asked for.
                </p>
              </Card>

              <Modal
                open={addOpen}
                onClose={() => setAddOpen(false)}
                title="Add a donor"
                footer={
                  <>
                    <button
                      className="btn btn-primary"
                      onClick={add}
                      disabled={busy || !email.includes('@') || !d.config.donorUrl}
                    >
                      {busy ? <Spinner /> : 'Create and send an invitation'}
                    </button>
                    <button className="btn" onClick={() => setAddOpen(false)}>
                      Cancel
                    </button>
                  </>
                }
              >
                <div className="col">
                  <Notice icon="info">
                    {d.config.mailConfigured
                      ? 'They are emailed an invitation to choose a password. The account authorizes ' +
                        'nobody until they do. You will also be shown the link, in case it needs ' +
                        'sending another way.'
                      : 'Email is not configured, so nothing is sent. You will be shown a one-time ' +
                        'link to pass on yourself, and the account authorizes nobody until they ' +
                        'follow it and choose a password.'}
                  </Notice>
                  <Field label="Email" required hint="Any address — this is their sign-in name.">
                    <input
                      className="input"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="friend@example.com"
                    />
                  </Field>
                  <Field label="Name" hint="Used to greet them in the invitation.">
                    <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
                  </Field>
                </div>
              </Modal>

              {/* Picking a donor the register already knows. The list is addresses that
                  have no account yet; the register's name-only donors cannot be offered
                  one at all, and the footnote says how many of those there are rather
                  than leaving somebody hunting for a name that is never going to appear. */}
              <Modal
                open={pickOpen}
                onClose={() => {
                  setPickOpen(false)
                  setPicked(null)
                }}
                title="Add a donor from the giving register"
                footer={
                  <>
                    <button
                      className="btn btn-primary"
                      onClick={addFromRegister}
                      disabled={busy || !picked || !d.config.donorUrl}
                    >
                      {busy ? (
                        <Spinner />
                      ) : picked ? (
                        /* The name is already on the highlighted row, so the button spends
                           its width saying what will happen instead of repeating it — and
                           "follow 13" on its own does not say 13 of what. */
                        `Create the account and follow ${fmtInt(picked.unit_count)} ${t(
                          picked.unit_count === 1 ? 'partner unit' : 'partner units'
                        )}`
                      ) : (
                        'Choose a donor'
                      )}
                    </button>
                    <button
                      className="btn"
                      onClick={() => {
                        setPickOpen(false)
                        setPicked(null)
                      }}
                    >
                      Cancel
                    </button>
                  </>
                }
              >
                <div className="col">
                  <Notice icon="info">
                    The account is made with the address and name the register holds, and the{' '}
                    {t('partner units')} they have given to become the ones they follow — so their
                    Favorites are already the units they support the first time they sign in. They
                    are invited exactly as a typed address is, and authorize nobody until they
                    choose a password.
                  </Notice>
                  <SearchInput
                    value={pickQ}
                    onChange={setPickQ}
                    placeholder="Search the register by name or address…"
                    aria-label="Search the donor register"
                  />
                  {candidates.isLoading ? (
                    <Spinner label="Reading the register…" />
                  ) : !candidates.data?.rows.length ? (
                    <p className="small subtle">
                      {pickQ
                        ? 'No donor in the register matches that, or they already have an account.'
                        : 'Every donor in the register with an address already has an account.'}
                    </p>
                  ) : (
                    <div className="table-wrap" style={{ maxHeight: 320, overflowY: 'auto' }}>
                      <table className="data compact">
                        <thead>
                          <tr>
                            <th />
                            <th>Donor</th>
                            <th>Email</th>
                            <th className="right">Units given to</th>
                          </tr>
                        </thead>
                        <tbody>
                          {candidates.data.rows.map((r) => (
                            <tr
                              key={r.id}
                              onClick={() => setPicked(r)}
                              style={{
                                cursor: 'pointer',
                                background: picked?.id === r.id ? 'var(--bg-sunken)' : undefined,
                              }}
                            >
                              <td>
                                <input
                                  type="radio"
                                  name="registry-donor"
                                  checked={picked?.id === r.id}
                                  onChange={() => setPicked(r)}
                                />
                              </td>
                              <td className="small">{r.preferred_name || '—'}</td>
                              <td className="small subtle">{r.email}</td>
                              <td className="num">{fmtInt(r.unit_count)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {!!candidates.data?.totals?.without_email && (
                    <p className="small subtle" style={{ margin: 0 }}>
                      {fmtInt(candidates.data.totals.without_email)} of the{' '}
                      {fmtInt(candidates.data.totals.in_register)} donors in the register have no
                      email address on file and cannot be given an account — an address is the
                      sign-in name. {fmtInt(candidates.data.totals.already_have_one)} already have
                      one.
                    </p>
                  )}
                </div>
              </Modal>

              <ConfirmModal
                open={syncOpen}
                onClose={() => setSyncOpen(false)}
                onConfirm={syncAll}
                busy={busy}
                confirmLabel={`Follow ${fmtInt(d.pending?.units ?? 0)} more`}
                title="Sync every account with the giving register?"
                body={
                  <>
                    <p>
                      {fmtInt(d.pending?.accounts ?? 0)} account
                      {d.pending?.accounts === 1 ? '' : 's'} give to{' '}
                      {t('partner units')} they are not following. Those{' '}
                      {fmtInt(d.pending?.units ?? 0)} will be added to their Favorites.
                    </p>
                    <p style={{ marginTop: 10 }}>
                      <strong>Nothing is removed.</strong> A donor who unfollowed a{' '}
                      {t('partner unit')}, or followed one the register has never heard of,
                      made that choice about their own page — so anything they follow that
                      the register does not have is left exactly as it is.
                    </p>
                    {!!d.pending?.unregistered && (
                      <p style={{ marginTop: 10, color: 'var(--fg-muted)' }}>
                        {fmtInt(d.pending.unregistered)} account
                        {d.pending.unregistered === 1 ? ' is' : 's are'} not in the register at
                        all and {d.pending.unregistered === 1 ? 'is' : 'are'} untouched.
                      </p>
                    )}
                  </>
                }
              />

              <Modal
                open={!!editing}
                onClose={() => setEditing(null)}
                title={`Edit ${editing?.row.email}`}
                footer={
                  <>
                    <button className="btn btn-primary" onClick={saveName} disabled={busy}>
                      {busy ? <Spinner /> : 'Save'}
                    </button>
                    <button className="btn" onClick={() => setEditing(null)}>
                      Cancel
                    </button>
                  </>
                }
              >
                {editing && (
                  <Field
                    label="Name"
                    htmlFor="donor-name"
                    hint="How the list shows them and how their invitation and reset emails greet them. Leave it empty to greet them with a plain “Hello”."
                  >
                    <input
                      id="donor-name"
                      className="input"
                      maxLength={100}
                      value={editing.name}
                      onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                      onKeyDown={(e) => e.key === 'Enter' && !busy && saveName()}
                      autoFocus
                    />
                  </Field>
                )}
              </Modal>

              <ConfirmModal
                open={!!confirmDelete}
                onClose={() => setConfirmDelete(null)}
                onConfirm={remove}
                busy={busy}
                confirmLabel="Remove account"
                title={`Remove ${confirmDelete?.email}?`}
                body={
                  <>
                    <p>
                      The account, its password, any open sessions and any invitation or reset link
                      still outstanding all go at once. They are signed out immediately.
                    </p>
                    <p style={{ marginTop: 10, color: 'var(--fg-muted)' }}>
                      To stop them signing in without losing the account, switch it off instead — the
                      effect is the same and it can be undone.
                    </p>
                  </>
                }
              />
            </div>
          )
        }}
      </QueryState>
    </PageHeader>
  )
}
